import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { useGraphql } from 'utils/hooks/useGraphql';
import { isNonEmptyArray, isNotNull } from 'utils/tsHelpers';

import { LIST_METADATA_FRAGMENT } from './constants';
import { ListMetadata } from './types';

const createQueryForListId = (listId: number) => `
  list_${listId}:list(id:${listId}) {
    ...ListMetadata
  }
`;

/**
 *
 * Note: "createQueryForListIds" won't create a valid query if listIds is empty.
 * We also can't skip the gql call unless the query itself is valid.
 * To resolve this we can pass an array with an psuedo id (ie: [0]) to satisfy the query in order to skip
 * the gql call. The actual query id doesn't really matter since it will be skipped.
 */

const createQueryForListIds = (listIds: number[]) => gql`
  ${LIST_METADATA_FRAGMENT}

  query ListsById {
    ${listIds.sort().map(createQueryForListId).join('\n')}
  }
`;

const emptyQueryIds = [0];

export const useListsByIds = (listIds: number[]) => {
  const query = createQueryForListIds(isNonEmptyArray(listIds) ? listIds : emptyQueryIds);
  const {
    data: results,
    error: didError,
    isLoading,
  } = useGraphql<Record<`list_${number}`, ListMetadata | null>>(query, {
    skip: !isNonEmptyArray(listIds),
  });
  const lists = useMemo(() => Object.values(results ?? {}).filter(isNotNull), [results]);
  return { lists, didError, isLoading };
};
